import React, { useState, useEffect } from 'react';
import { PersonDetailData } from '../index';
import { Car, Calendar, Plus, Tag, Users, ExternalLink, RefreshCw, ShieldOff, Trash2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { LicensePlate } from '../../ui/LicensePlate';
import { VehicleFormModal } from '../../OperationalView/components/VehicleFormModal';
import { VehicleDetailModal } from '../../OperationalView/components/VehicleDetailModal';
import { RfidDevicesSection } from './RfidDevicesSection';
import { api, fetchWithAuth } from '../../../services/api';
import { registerOnDeviceWithMask, deleteFromDevice } from '../../../services/guaritaIntegrationService';
import { syncVehicleRfid } from '../../../utils/vehicleRfidSync';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { usePersonDetailContext } from '../../../contexts/PersonDetailContext';
import { DeviceInfo, DeviceOperations, CommunicationMode } from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import toast from 'react-hot-toast';

const ownerTypeLabel = (type?: string): string => {
  const map: Record<string, string> = {
    resident: 'Morador',
    employee: 'Funcionário',
    staff: 'Funcionário',
    visitor: 'Visitante',
    service_provider: 'Prestador de Serviço',
    company: 'Empresa',
    delivery: 'Entregador',
  };
  return map[(type || '').toLowerCase()] || type || '';
};

interface VehiclesTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  darkMode?: boolean;
}

interface Vehicle {
  id: string;
  plate: string;
  rfid_tag?: string;
  brand?: string;
  model?: string;
  color?: string;
  year?: number;
  owner_type?: string;
  owner_name?: string;
  person_id?: string;
  unit_id?: string;
  photo_url?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  person?: { id: string; first_name: string; last_name: string };
}

interface VehicleRowProps {
  vehicle: Vehicle;
  darkMode: boolean;
  isMine?: boolean;
  onPersonClick?: (personId: string) => void;
  onVehicleClick?: (vehicle: Vehicle) => void;
  onSync?: (vehicle: Vehicle) => void;
  onRevoke?: (vehicle: Vehicle) => void;
  onDelete?: (vehicle: Vehicle) => void;
  syncing?: boolean;
  revoking?: boolean;
}

const VehicleRow: React.FC<VehicleRowProps> = ({ vehicle, darkMode, isMine = true, onPersonClick, onVehicleClick, onSync, onRevoke, onDelete, syncing, revoking }) => {
  const photoUrl = vehicle.photo_url ? getAbsoluteImageUrl(vehicle.photo_url) : null;

  const descParts = [vehicle.brand, vehicle.model, vehicle.color, vehicle.year].filter(Boolean);
  const description = descParts.join(' · ') || null;

  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 rounded-xl border transition-all',
        onVehicleClick ? 'cursor-pointer' : '',
        vehicle.is_active
          ? darkMode
            ? 'bg-gray-900 border-gray-700 hover:border-gray-600'
            : 'bg-white border-gray-200 hover:border-gray-300'
          : darkMode
          ? 'bg-gray-800/60 border-gray-700 opacity-60'
          : 'bg-gray-50 border-gray-200 opacity-60',
      )}
      onClick={onVehicleClick ? () => onVehicleClick(vehicle) : undefined}
    >
      {/* Vehicle photo or plate icon */}
      <div className="flex-shrink-0">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={vehicle.plate}
            className="w-20 h-14 object-cover rounded-lg border border-gray-200"
          />
        ) : (
          <div className={cn(
            'w-20 h-14 rounded-lg flex items-center justify-center',
            darkMode ? 'bg-gray-800' : 'bg-gray-100',
          )}>
            <Car className={cn('w-7 h-7', darkMode ? 'text-gray-500' : 'text-gray-400')} />
          </div>
        )}
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <LicensePlate plate={vehicle.plate} size="md" />
          <span className={cn(
            'px-2 py-0.5 rounded-full text-xs font-medium',
            vehicle.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600',
          )}>
            {vehicle.is_active ? 'Ativo' : 'Inativo'}
          </span>
        </div>

        {/* Owner name — prominently below plate for unit-mate vehicles */}
        {!isMine && (vehicle.person || vehicle.owner_name) && (
          vehicle.person && onPersonClick ? (
            <button
              onClick={() => onPersonClick(vehicle.person!.id)}
              className={cn(
                'flex items-center gap-1 text-sm font-medium mt-0.5 transition-colors hover:underline',
                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700',
              )}
            >
              {vehicle.person.first_name} {vehicle.person.last_name}
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </button>
          ) : (
            <p className={cn('text-sm font-medium mt-0.5', darkMode ? 'text-blue-400' : 'text-blue-600')}>
              {vehicle.owner_name}
            </p>
          )
        )}

        {description && (
          <p className={cn('text-sm mt-0.5 truncate', darkMode ? 'text-gray-400' : 'text-gray-500')}>
            {description}
          </p>
        )}

        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          {vehicle.rfid_tag && (
            <span className={cn('flex items-center gap-1 text-xs font-mono', darkMode ? 'text-gray-400' : 'text-gray-500')}>
              <Tag className="w-3 h-3" />
              {vehicle.rfid_tag}
            </span>
          )}
          {vehicle.owner_type && (
            <span className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
              {ownerTypeLabel(vehicle.owner_type)}
            </span>
          )}
          <span className={cn('flex items-center gap-1 text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
            <Calendar className="w-3 h-3" />
            {new Date(vehicle.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        </div>

        {vehicle.notes && (
          <p className={cn('text-xs mt-1 italic truncate', darkMode ? 'text-gray-500' : 'text-gray-400')}>
            {vehicle.notes}
          </p>
        )}
      </div>

      {/* Sync / Revoke / Delete action buttons */}
      {(onSync || onRevoke || onDelete) && (
        <div className="flex flex-col gap-1 flex-shrink-0">
          {onSync && (
            <button
              onClick={(e) => { e.stopPropagation(); onSync(vehicle); }}
              disabled={syncing}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50',
                darkMode
                  ? 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/30'
                  : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
              )}
              title="Sincronizar com leitores"
            >
              <RefreshCw className={cn('h-3 w-3', syncing && 'animate-spin')} />
              Sincronizar
            </button>
          )}
          {onRevoke && (
            <button
              onClick={(e) => { e.stopPropagation(); onRevoke(vehicle); }}
              disabled={revoking}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50',
                darkMode
                  ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30'
                  : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
              )}
              title="Revogar acesso"
            >
              <ShieldOff className={cn('h-3 w-3', revoking && 'animate-pulse')} />
              Revogar
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(vehicle); }}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                darkMode
                  ? 'bg-gray-800 hover:bg-red-500/10 text-gray-500 hover:text-red-400 border-gray-700 hover:border-red-500/30'
                  : 'bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-600 border-gray-200 hover:border-red-200'
              )}
              title="Excluir veículo"
            >
              <Trash2 className="h-3 w-3" />
              Excluir
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const VehiclesTab: React.FC<VehiclesTabProps> = ({ person, onRefresh, darkMode = false }) => {
  const { openPersonDetail } = usePersonDetailContext();
  const vehicles = (person.vehicles || []) as Vehicle[];
  const [showAddModal, setShowAddModal] = useState(false);
  // The rows here have no edit button, so the only way in is the detail modal's "Editar".
  // That handler used to just close the detail modal, leaving nowhere to edit a vehicle
  // reached through a person.
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [unitVehicles, setUnitVehicles] = useState<Vehicle[]>([]);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [vehicleDetail, setVehicleDetail] = useState<Vehicle | null>(null);
  const [syncingVehicleId, setSyncingVehicleId] = useState<string | null>(null);
  const [revokingVehicleId, setRevokingVehicleId] = useState<string | null>(null);

  const customerId = person.customer?.id || '';

  const syncOrRevoke = async (vehicle: Vehicle, isAllowed: boolean) => {
    if (!customerId) return;
    const setState = isAllowed ? setSyncingVehicleId : setRevokingVehicleId;
    setState(vehicle.id);
    try {
      await syncVehicleRfid({ customerId, vehicle, isAllowed });
    } finally {
      setState(null);
    }
  };

  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    if (!customerId) return;
    if (!window.confirm(`Deseja realmente excluir o veículo ${vehicle.plate}?`)) return;
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${customerId}/vehicles/${vehicle.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Veículo excluído!');
        onRefresh();
      } else {
        toast.error('Erro ao excluir veículo');
      }
    } catch {
      toast.error('Erro ao excluir veículo');
    }
  };

  const roles = person.roles || [];
  const isResident = roles.some(r => r.role_type === 'resident' || r.role_type === 'morador');
  const isStaff = roles.some(r => r.role_type === 'staff' || r.role_type === 'funcionario');
  const isVisitor = roles.some(r => r.role_type === 'visitor' || r.role_type === 'visitante');
  const isServiceProvider = roles.some(r => r.role_type === 'service_provider' || r.role_type === 'prestador');
  // Any owner type VehicleFormModal itself supports — previously only resident/staff could
  // add a vehicle here, silently hiding the button for visitantes/prestadores.
  const canAddVehicle = (isResident || isStaff || isVisitor || isServiceProvider) && !!person.customer?.id;
  const ownerType: 'resident' | 'employee' | 'visitor' | 'service_provider' =
    isStaff ? 'employee' : isServiceProvider ? 'service_provider' : isVisitor ? 'visitor' : 'resident';
  const customerType = (person.customer?.type as 'condominio' | 'empresa') || 'condominio';

  // Collect all unit IDs from resident role
  const residentRole = roles.find(r => r.role_type === 'resident' || r.role_type === 'morador');
  const unitIds: string[] = residentRole?.role_metadata?.unit_assignments
    ?.map((a: any) => a.unit_id)
    .filter(Boolean) ||
    (residentRole?.role_metadata?.unit_id ? [residentRole.role_metadata.unit_id] : []);

  // Fetch other vehicles from the same unit(s)
  useEffect(() => {
    if (!person.customer?.id || unitIds.length === 0) return;

    const myVehicleIds = new Set(vehicles.map(v => v.id));

    const fetchAll = async () => {
      setLoadingUnit(true);
      try {
        const results = await Promise.all(
          unitIds.map(uid =>
            api.get(`/customers/${person.customer!.id}/vehicles`, { params: { unit_id: uid, page_size: 50 } })
              .then(r => (r.data?.vehicles || []) as Vehicle[])
              .catch(() => [] as Vehicle[])
          )
        );
        const merged = results.flat().filter(v => !myVehicleIds.has(v.id));
        // Deduplicate by id
        const seen = new Set<string>();
        const unique = merged.filter(v => { if (seen.has(v.id)) return false; seen.add(v.id); return true; });
        setUnitVehicles(unique);
      } finally {
        setLoadingUnit(false);
      }
    };

    fetchAll();
  }, [person.customer?.id, unitIds.join(',')]);

  const addButton = canAddVehicle && (
    <button
      onClick={() => setShowAddModal(true)}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
        darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white',
      )}
    >
      <Plus className="w-4 h-4" />
      Adicionar Veículo
    </button>
  );

  if (vehicles.length === 0 && unitVehicles.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className={cn('w-20 h-20 rounded-full flex items-center justify-center mb-4', darkMode ? 'bg-gray-800' : 'bg-gray-100')}>
            <Car className={cn('w-10 h-10', darkMode ? 'text-gray-500' : 'text-gray-400')} />
          </div>
          <h3 className={cn('text-lg font-semibold mb-2', darkMode ? 'text-gray-100' : 'text-gray-900')}>
            Nenhum Veículo
          </h3>
          <p className={cn('text-center max-w-md mb-4', darkMode ? 'text-gray-400' : 'text-gray-600')}>
            Esta pessoa ainda não possui veículos registrados.
          </p>
          {addButton}
        </div>
        <div className="mt-6">
          <RfidDevicesSection person={person} darkMode={darkMode} />
        </div>
        {showAddModal && person.customer?.id && (
          <VehicleFormModal
            open={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSuccess={() => { setShowAddModal(false); onRefresh(); }}
            customerId={person.customer.id}
            customerType={customerType}
            darkMode={darkMode}
            preselectedPerson={{
              id: person.id,
              first_name: person.first_name,
              last_name: person.last_name,
              email: person.email,
              phone: person.phone,
              identity_photo_url: (person as any).identity_photo_url,
            }}
            preselectedOwnerType={ownerType}
            condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Person's own vehicles */}
        <div>
          <div className="flex items-center justify-end mb-3">
            {addButton}
          </div>

          {vehicles.length === 0 ? (
            <p className={cn('text-sm py-4 text-center', darkMode ? 'text-gray-500' : 'text-gray-400')}>
              Nenhum veículo registrado para esta pessoa.
            </p>
          ) : (
            <div className="space-y-3">
              {vehicles.map(v => (
                <VehicleRow
                  key={v.id}
                  vehicle={v}
                  darkMode={darkMode}
                  isMine
                  onVehicleClick={(vehicle) => setVehicleDetail({ ...vehicle, person_id: person.id, person: { id: person.id, first_name: person.first_name, last_name: person.last_name } })}
                  onSync={(vehicle) => syncOrRevoke({ ...vehicle, person_id: person.id }, true)}
                  onRevoke={(vehicle) => syncOrRevoke({ ...vehicle, person_id: person.id }, false)}
                  onDelete={(vehicle) => handleDeleteVehicle(vehicle)}
                  syncing={syncingVehicleId === v.id}
                  revoking={revokingVehicleId === v.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Other unit vehicles */}
        {(unitVehicles.length > 0 || loadingUnit) && (
          <div>
            <div className={cn('flex items-center gap-2 mb-3 pb-3 border-b', darkMode ? 'border-gray-700' : 'border-gray-100')}>
              <Users className={cn('w-4 h-4', darkMode ? 'text-gray-400' : 'text-gray-500')} />
              <h3 className={cn('text-sm font-semibold', darkMode ? 'text-gray-300' : 'text-gray-600')}>
                Outros veículos da mesma unidade
              </h3>
              {!loadingUnit && (
                <span className={cn('text-xs px-1.5 py-0.5 rounded-full', darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')}>
                  {unitVehicles.length}
                </span>
              )}
            </div>

            {loadingUnit ? (
              <div className={cn('text-sm text-center py-4', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                Carregando...
              </div>
            ) : (
              <div className="space-y-3">
                {unitVehicles.map(v => (
                  <VehicleRow
                    key={v.id}
                    vehicle={v}
                    darkMode={darkMode}
                    isMine={false}
                    onPersonClick={openPersonDetail}
                    onVehicleClick={(vehicle) => setVehicleDetail(vehicle)}
                    onSync={(vehicle) => syncOrRevoke(vehicle, true)}
                    onRevoke={(vehicle) => syncOrRevoke(vehicle, false)}
                    syncing={syncingVehicleId === v.id}
                    revoking={revokingVehicleId === v.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* RFID / TAG Reader Devices */}
      <div className="mt-6">
        <RfidDevicesSection person={person} darkMode={darkMode} />
      </div>

      {/* Vehicle Detail Modal */}
      {vehicleDetail && person.customer?.id && (
        <VehicleDetailModal
          open={!!vehicleDetail}
          vehicle={vehicleDetail}
          customerId={person.customer.id}
          condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
          onClose={() => setVehicleDetail(null)}
          onEdit={(v) => { setVehicleDetail(null); setEditingVehicle(v as Vehicle); }}
          onSync={(v) => syncOrRevoke(v as any, true)}
          isSyncing={!!(vehicleDetail && syncingVehicleId === vehicleDetail.id)}
          onRevoke={(v) => syncOrRevoke(v as any, false)}
          isRevoking={!!(vehicleDetail && revokingVehicleId === vehicleDetail.id)}
          onDelete={(v) => { setVehicleDetail(null); handleDeleteVehicle(v as any); }}
          onPersonClick={(id) => { setVehicleDetail(null); openPersonDetail(id); }}
          lightTheme={!darkMode}
        />
      )}

      {/* Add Vehicle Modal */}
      {showAddModal && person.customer?.id && (
        <VehicleFormModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); onRefresh(); }}
          customerId={person.customer.id}
          customerType={customerType}
          darkMode={darkMode}
          preselectedPerson={{
            id: person.id,
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email,
            phone: person.phone,
            identity_photo_url: (person as any).identity_photo_url,
          }}
          preselectedOwnerType={ownerType}
          condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
        />
      )}

      {/* Edit Vehicle Modal */}
      {editingVehicle && person.customer?.id && (
        <VehicleFormModal
          open={!!editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSuccess={() => { setEditingVehicle(null); onRefresh(); }}
          customerId={person.customer.id}
          customerType={customerType}
          darkMode={darkMode}
          editingVehicle={editingVehicle as any}
          condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
        />
      )}
    </>
  );
};
